class ThemeNavigation extends HTMLElement {
  constructor() {
    super();
    this.activeSubmenu = null;
    this.submenuTrigger = null;
    this.onPointerOver = this.onPointerOver.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.reset = this.reset.bind(this);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  motionDuration(name, fallback) {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!value) return fallback;
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return fallback;
    return value.endsWith('ms') ? parsed : parsed * 1000;
  }

  connectedCallback() {
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('navigation:reset', this.reset);
    this.syncActiveHeaderLink();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('navigation:reset', this.reset);
  }

  onPointerOver(event) {
    const clearTrigger = event.target.closest('[data-menu-preview-clear]');
    if (clearTrigger) {
      this.clearPreview(clearTrigger);
      return;
    }

    this.activatePreview(event.target.closest('[data-menu-preview-trigger]'));
  }

  onFocusIn(event) {
    const clearTrigger = event.target.closest('[data-menu-preview-clear]');
    if (clearTrigger) {
      this.clearPreview(clearTrigger);
      return;
    }

    this.activatePreview(event.target.closest('[data-menu-preview-trigger]'));
  }

  onClick(event) {
    const openTrigger = event.target.closest('[data-menu-submenu-open]');
    if (openTrigger) {
      event.preventDefault();
      this.openSubmenu(openTrigger);
      return;
    }

    if (event.target.closest('[data-menu-submenu-back]')) {
      event.preventDefault();
      this.closeSubmenu();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.activeSubmenu) {
      event.preventDefault();
      event.stopPropagation();
      this.closeSubmenu();
    }
  }

  activatePreview(trigger) {
    if (!trigger) return;

    const root = trigger.closest('[data-menu-preview-root]');
    if (!root) return;

    root.querySelectorAll('[data-menu-preview-panel]').forEach((panel) => {
      const isActive = panel.dataset.menuPreviewPanel === trigger.dataset.menuPreviewTrigger;
      panel.hidden = !isActive;
      panel.toggleAttribute('data-preview-entering', isActive);

      if (isActive) {
        requestAnimationFrame(() => panel.removeAttribute('data-preview-entering'));
      }
    });
  }

  clearPreview(trigger) {
    const root = trigger.closest('[data-menu-preview-root]');
    if (!root) return;

    root.querySelectorAll('[data-menu-preview-panel]').forEach((panel) => {
      panel.hidden = true;
      panel.removeAttribute('data-preview-entering');
    });
  }

  syncActiveHeaderLink() {
    const header = this.closest('.site-header--menu-underline');
    if (!header || !this.classList.contains('category-nav--header')) return;

    const topLinks = this.getTopLevelLinks();
    if (!topLinks.length) return;

    const currentUrl = this.normalizeMenuUrl(window.location.href);
    const allProductsUrl = this.normalizeMenuUrl(this.dataset.allProductsUrl || '/collections/all');
    const collectionsIndexUrl = this.normalizeMenuUrl(this.dataset.collectionsIndexUrl || '/collections');
    const isProductsIndex = (url) => {
      return url.path === allProductsUrl.path || url.path === collectionsIndexUrl.path;
    };

    let activeLink = null;

    if (currentUrl.search) {
      activeLink = this.findExactLink(topLinks, currentUrl);
      activeLink = activeLink || this.findQuerySubsetLink(topLinks, currentUrl);
      activeLink = activeLink || this.findExactChildLink(topLinks, currentUrl);
      activeLink = activeLink || this.findQuerySubsetChildLink(topLinks, currentUrl);
    }

    if (!activeLink && isProductsIndex(currentUrl)) {
      activeLink = this.findProductsIndexParent(topLinks, isProductsIndex);
    }

    activeLink = activeLink || this.findExactLink(topLinks, currentUrl);
    activeLink = activeLink || this.findExactChildLink(topLinks, currentUrl);

    topLinks.forEach((link) => link.classList.toggle('is-active', link === activeLink));
  }

  getTopLevelLinks() {
    return Array.from(
      this.querySelectorAll(
        ':scope > ul > .category-nav__item > a, :scope > ul > .category-nav__item > .category-nav__details > .category-nav__summary'
      )
    );
  }

  normalizeMenuUrl(value) {
    const url = new URL(value, window.location.origin);
    let path = url.pathname || '/';

    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }

    return {
      path,
      search: this.normalizeSearch(url.search),
    };
  }

  normalizeSearch(search) {
    if (!search) return '';

    const pairs = Array.from(new URLSearchParams(search).entries())
      .filter(([key]) => key)
      .sort(([keyA, valueA], [keyB, valueB]) => {
        if (keyA === keyB) return valueA.localeCompare(valueB);
        return keyA.localeCompare(keyB);
      });

    if (!pairs.length) return '';

    return pairs
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
  }

  urlsMatch(link, currentUrl) {
    const linkUrl = this.normalizeMenuUrl(link.href);
    return linkUrl.path === currentUrl.path && linkUrl.search === currentUrl.search;
  }

  isQuerySubset(link, currentUrl) {
    const linkUrl = this.normalizeMenuUrl(link.href);
    if (linkUrl.path !== currentUrl.path || !linkUrl.search || !currentUrl.search) return false;

    const currentPairs = new Set(currentUrl.search.split('&'));
    return linkUrl.search.split('&').every((pair) => currentPairs.has(pair));
  }

  findExactLink(topLinks, currentUrl) {
    return topLinks.find((link) => this.urlsMatch(link, currentUrl)) || null;
  }

  findQuerySubsetLink(topLinks, currentUrl) {
    return topLinks.find((link) => this.isQuerySubset(link, currentUrl)) || null;
  }

  findExactChildLink(topLinks, currentUrl) {
    for (const topLink of topLinks) {
      const childLinks = this.getChildLinks(topLink);
      if (childLinks.some((childLink) => this.urlsMatch(childLink, currentUrl))) {
        return topLink;
      }
    }

    return null;
  }

  findQuerySubsetChildLink(topLinks, currentUrl) {
    for (const topLink of topLinks) {
      const childLinks = this.getChildLinks(topLink);
      if (childLinks.some((childLink) => this.isQuerySubset(childLink, currentUrl))) {
        return topLink;
      }
    }

    return null;
  }

  getChildLinks(topLink) {
    const item = topLink.closest('.category-nav__item');
    if (!item) return [];

    return Array.from(item.querySelectorAll('a[href]')).filter((link) => link !== topLink);
  }

  findProductsIndexParent(topLinks, isProductsIndex) {
    return topLinks.find((link) => {
      const item = link.closest('.category-nav__item');
      return item?.classList.contains('category-nav__item--has-children') && isProductsIndex(this.normalizeMenuUrl(link.href));
    }) || null;
  }

  openSubmenu(trigger) {
    const submenu = this.querySelector('#' + CSS.escape(trigger.dataset.menuSubmenuOpen));
    const mainPanel = this.querySelector('[data-menu-panel="main"]');
    if (!submenu || !mainPanel) return;

    this.activeSubmenu = submenu;
    this.submenuTrigger = trigger;
    submenu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    if (this.prefersReducedMotion()) {
      mainPanel.hidden = true;
    } else {
      mainPanel.dataset.menuPanelState = 'leaving';
      submenu.dataset.menuPanelState = 'entering';

      requestAnimationFrame(() => {
        submenu.dataset.menuPanelState = 'active';
      });

      window.setTimeout(() => {
        mainPanel.hidden = true;
        mainPanel.dataset.menuPanelState = '';
      }, this.motionDuration('--motion-duration-base', 180));
    }

    const drawer = this.closest('.theme-drawer');
    const drawerBack = drawer?.querySelector('[data-menu-drawer-back]');
    const drawerTitle = drawer?.querySelector('[data-drawer-title]');
    if (drawerBack) drawerBack.hidden = false;
    if (drawerTitle) drawerTitle.hidden = true;
    drawerBack?.focus();
  }

  closeSubmenu(restoreFocus = true) {
    if (!this.activeSubmenu) return;

    const mainPanel = this.querySelector('[data-menu-panel="main"]');
    const submenu = this.activeSubmenu;
    if (mainPanel) mainPanel.hidden = false;
    this.submenuTrigger?.setAttribute('aria-expanded', 'false');

    if (this.prefersReducedMotion() || !mainPanel) {
      submenu.hidden = true;
    } else {
      submenu.dataset.menuPanelState = 'leaving';
      mainPanel.dataset.menuPanelState = 'entering';

      requestAnimationFrame(() => {
        mainPanel.dataset.menuPanelState = 'active';
      });

      window.setTimeout(() => {
        submenu.hidden = true;
        submenu.dataset.menuPanelState = '';
        mainPanel.dataset.menuPanelState = '';
      }, this.motionDuration('--motion-duration-base', 180));
    }

    const drawer = this.closest('.theme-drawer');
    const drawerBack = drawer?.querySelector('[data-menu-drawer-back]');
    const drawerTitle = drawer?.querySelector('[data-drawer-title]');
    if (drawerBack) drawerBack.hidden = true;
    if (drawerTitle) drawerTitle.hidden = false;

    const trigger = this.submenuTrigger;
    this.activeSubmenu = null;
    this.submenuTrigger = null;
    if (restoreFocus) trigger?.focus();
  }

  reset() {
    this.closeSubmenu(false);
  }
}

if (!customElements.get('theme-navigation')) {
  customElements.define('theme-navigation', ThemeNavigation);
}
